import { useEffect } from 'react';
import { useOperatorStore } from '@/core/stores/operator-store';
import { useActorStore } from '@/features/site-management/stores/actor-store';
import { useAuthStore } from '@/core/stores/auth-store';
import { Button } from '@/core/components/ui/button';
import { TreePine, Filter } from 'lucide-react';
import { ActorSelector } from '@/core/components/ActorSelector';
import { LocationSelector } from '@/core/components/LocationSelector';

interface DashboardHeaderProps {
  selectedLocation: string;
  onLocationChange: (value: string) => void;
  onLocationSelect?: (actorId: string) => void;
  showLocationSelector?: boolean;
  onToggleTreeView?: () => void;
  isTreeViewVisible?: boolean;
  selectedActorId?: string; // Add to pass to LocationSelector
  skipLocationSelectorApiCall?: boolean; // Flag to prevent API calls from ActorTreeView selections
  autoSelectFirstLocation?: boolean; // Flag to enable/disable auto-selection of first location (default: true)
  isLocationSelectorDisabled?: boolean; // Flag to disable location selector (for grouped view)
  hasActiveFilters?: boolean; // Flag to indicate if filters are active

  // NEW: Filter props (operator view only)
  operatorStatusFilter?: string[];
  operatorOnlineOnly?: boolean;
  onStatusFilterChange?: (statuses: string[] | undefined) => void;
  onOnlineOnlyChange?: (onlineOnly: boolean) => void;
  viewMode?: 'single' | 'operator';
}

export function DashboardHeader({
  selectedLocation,
  onLocationChange,
  onLocationSelect,
  showLocationSelector = true,
  onToggleTreeView,
  isTreeViewVisible = false,
  selectedActorId,
  skipLocationSelectorApiCall = false,
  autoSelectFirstLocation = false, // Changed to false to always show "All Locations" option
  isLocationSelectorDisabled = false,
  hasActiveFilters = false,
  // NEW: Filter props (operator view only)
  operatorStatusFilter,
  operatorOnlineOnly = false,
  onStatusFilterChange,
  onOnlineOnlyChange,
  viewMode = 'single'
}: DashboardHeaderProps) {
  const { selectedOperator, operators, isLoading, error, selectOperator, loadOperators } = useOperatorStore(); // Get all needed state from store
  const { isLoading: actorsLoading } = useActorStore(); // Get actors loading state
  const { isDistributor } = useAuthStore();
  const isProduction = import.meta.env.PROD;

  // Load operators on mount for distributors
  useEffect(() => {
    if (isDistributor && operators.length === 0 && !isLoading) {
      loadOperators();
    }
  }, [isDistributor]);

  const handleOperatorChange = (operatorId: string) => {
    // Find the selected operator
    const selectedOp = operators.find(op => op.operatorId.toString() === operatorId);
    if (selectedOp) {
      // Update the operator store
      selectOperator(selectedOp);
    }

    // Reset location selection when operator changes
    onLocationChange('');
    if (onLocationSelect) {
      onLocationSelect('');
    }
  };

  const handleLocationChange = (locationId: string) => {
    onLocationChange(locationId);
    // Call the API with the selected location's actorId
    if (onLocationSelect) {
      onLocationSelect(locationId);
    }
  };

  return (
    <div className="mb-6 mt-4">
      <div className="flex gap-4">
        {/* Only show operator selector for distributors with multiple operators */}
        {isDistributor && operators.length > 0 && (
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-700">Operator</label>
            <div className="flex gap-2 items-start">
              <ActorSelector
                operators={operators}
                selectedOperatorId={selectedOperator?.operatorId.toString() || ''}
                onOperatorChange={handleOperatorChange}
                isLoading={isLoading}
                className="w-80"
              />

            </div>
            {error && (
              <span className="text-xs text-red-600">Failed to load operators</span>
            )}
          </div>
        )}

        {showLocationSelector && selectedOperator && (
          <div className="flex flex-col gap-1">
            {/* Dynamic label based on tree view state */}
            <label className="text-sm font-medium text-gray-700">
              {isTreeViewVisible ? 'Hierarchy' : 'Location'}
            </label>
            <div className="flex gap-2 items-start">
              {/* Hide LocationSelector when tree view is visible */}
              {!isTreeViewVisible && (
                <LocationSelector
                  operatorId={selectedOperator.operatorId}
                  selectedLocationId={selectedLocation}
                  onLocationChange={handleLocationChange}
                  selectedActorId={selectedActorId}
                  skipApiCallOnActorChange={skipLocationSelectorApiCall}
                  isActorsLoading={actorsLoading}
                  className="w-80"
                  autoSelectFirst={autoSelectFirstLocation}
                  disabled={isLocationSelectorDisabled}
                />
              )}
              {onToggleTreeView && (
                <Button
                  variant={isTreeViewVisible ? "default" : "outline"}
                  size="sm"
                  onClick={onToggleTreeView}
                  className="px-3 py-2 h-10 flex items-center gap-2"
                  title={isTreeViewVisible ? "Switch to filter view" : "Switch to hierarchy view"}
                >
                  {isTreeViewVisible ? (
                    <>
                      <Filter className="h-4 w-4" />
                      <span className="text-sm">Filter</span>
                    </>
                  ) : (
                    <>
                      <TreePine className="h-4 w-4" />
                      <span className="text-sm">Hierarchy</span>
                    </>
                  )}
                </Button>
              )}
            </div>
            {isLocationSelectorDisabled && !isTreeViewVisible && (
              <span className="text-xs text-amber-600">
                Location filter is disabled when grouping by location
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}